import { useEffect, useLayoutEffect, useRef, useState } from "react"

export interface ContextMenuItem {
  readonly label: string
  readonly hint?: string
  /** An arrow or icon before the label (decorative). */
  readonly icon?: { readonly text: string; readonly tone: "up" | "down" | "muted" }
  readonly disabled?: boolean
  /** A rule above it, starting a group. */
  readonly separated?: boolean
  readonly onSelect: () => void
}

/**
 * A right-click menu at the pointer: Escape, a click elsewhere, scrolling or resizing closes it;
 * ↑ / ↓ move between items. Kept inside the window.
 */
export function ContextMenu({ items, onClose, x, y }: {
  readonly x: number
  readonly y: number
  readonly items: ReadonlyArray<ContextMenuItem>
  readonly onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState({ left: x, top: y })

  useLayoutEffect(() => {
    const menu = ref.current
    if (menu === null) return
    const { height, width } = menu.getBoundingClientRect()
    setAt({ left: Math.max(4, Math.min(x, window.innerWidth - width - 4)), top: Math.max(4, Math.min(y, window.innerHeight - height - 4)) })
    menu.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus()
  }, [x, y])

  useEffect(() => {
    const close = () => onClose()
    const outside = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return
      event.preventDefault()
      const buttons = [...(ref.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? [])]
      const index = buttons.indexOf(document.activeElement as HTMLButtonElement)
      const next = event.key === "ArrowDown" ? index + 1 : index - 1
      buttons[(next + buttons.length) % buttons.length]?.focus()
    }
    window.addEventListener("pointerdown", outside, true)
    window.addEventListener("keydown", onKey)
    window.addEventListener("resize", close)
    window.addEventListener("wheel", close, { passive: true })
    window.addEventListener("blur", close)
    return () => {
      window.removeEventListener("pointerdown", outside, true)
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("resize", close)
      window.removeEventListener("wheel", close)
      window.removeEventListener("blur", close)
    }
  }, [onClose])

  return (
    <div ref={ref} className="context-menu" role="menu" style={{ left: at.left, top: at.top }} onContextMenu={(event) => event.preventDefault()}>
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          role="menuitem"
          className={item.separated ? "context-menu-item context-menu-separated" : "context-menu-item"}
          disabled={item.disabled}
          onClick={() => {
            onClose()
            item.onSelect()
          }}
        >
          <span className="context-menu-label">
            {item.icon && <span className={`context-menu-icon ${item.icon.tone}`} aria-hidden>{item.icon.text}</span>}
            {item.label}
          </span>
          {item.hint && <span className="context-menu-hint">{item.hint}</span>}
        </button>
      ))}
    </div>
  )
}
